// src/components/news/NewsMiniCard.jsx
//
// News MiniCard — the front-page "opens in place" tier between NewsMiniGrid's MicroCard cell
// (unchanged) and the standalone ArticleDetail page (Detail, unchanged). Mirrors
// EventsMiniCard.jsx's contract and visual language deliberately (docs/briefs/
// 2026-09-04-news-minicard-phase2-build.md): same field-collapse rules, same border/shadow
// tokens, same inline-emphasis action-link — so a card that's read correctly as "the open one"
// on Events reads the same way here, and this stays the same card at any grid width (D11) rather
// than a bespoke layout that only survives when NewsMiniGrid hands it the full row width.
//
// This component has no opinion on positioning — NewsMiniGrid (the consumer) decides where the
// open MiniCard sits in the grid (full-width row, per the build brief) and owns open/close
// identity + scroll-into-view via the same useExpandInPlace/useScrollIntoViewOnOpen pair
// EventsColumn already uses. Click-to-collapse here matches EventsMiniCard: anywhere on the card
// collapses it except "More", which stops propagation so it navigates instead.
//
// Field set (design note addendum-1, ruled): image · title · introduction · author · comment
// count. "Introduction" is a UI label over the `summary` column, not a separate field (RESULTS
// §3) — same as Events.
//
// D9 empty-field rule:
//   HOLD     (title) — always occupies its slot; blank, never placeholder, when absent.
//   COLLAPSE (image, introduction, author, comment count) — the slot is not rendered at all when
//            the value is absent. Comment count also collapses at zero, not only when the field
//            itself is missing — a "0 comments" row reads as noise, not as a guaranteed field the
//            way Events' venue/date slots do, so it's treated as "nothing to show" the same as an
//            absent count. Flagged as a judgement call, same spirit as EventsMiniCard's own
//            border-weight note.
//
// The More affordance — unconditional on both surfaces (addendum-1 item 2, Michael's ruling,
// supersedes D8's "iff the record holds content beyond the introduction"). Detail always offers
// more than the MiniCard — comments and ratings here, plus event times/contact/RSVP on Events —
// even when a record's body is short, so gating the only route to it was guarding against a case
// that doesn't really occur. Same StandardButton inline-emphasis treatment EventsMiniCard's
// "Full details" uses, per the design note's ruled MiniCard action-link treatment — not a
// hand-rolled button.
//
// Video play-hint (docs/briefs/2026-09-04-news-video-play-hint-build.md) — video articles are
// identified purely by `video_url` being truthy, not by article_type (RESULTS §1: text and video
// share article_type='news'). Shown iff `article.video_url` — a centred play-arrow overlay on
// the image, its own click target, routing to the SAME onViewDetails prop "More" already uses.
// No autoplay: Detail is unchanged, the user presses play themselves — deliberately dropped by
// Michael (browser autoplay-across-navigation/permission issues aren't worth it). Not built on
// StandardButton: its shapes (inline/inline-emphasis, circle) are sized and coloured for a text
// link or an identity/admin avatar-scale control, neither of which fits a large icon-only overlay
// centred on an arbitrary-sized image — a small purpose-built button, using the Play icon already
// available from lucide-react (the same icon library StandardButton itself draws from), fits the
// same "don't hand-roll a glyph" instruction without forcing an ill-fitting shape through it.

import React from 'react';
import { Play } from 'lucide-react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { tokens } from '../../styles/designTokens';
import StandardButton from '../core/StandardButton';
import { BORDER_WIDTH } from '../events/EventsMiniCard';

const NewsMiniCard = ({ article, onViewDetails, onCollapse }) => {
  const theme = useTheme();
  const { t } = useTranslation('articles');

  // COLLAPSE — no placeholder graphic here (unlike NewsMiniGrid's own MicroCard row); an absent
  // image is a hidden slot per D9, same treatment as EventsMiniCard.
  const imageUrl = article.cover_image_url || null;

  const introduction = article.summary || '';

  // Author — COLLAPSE, but the fetched field is never actually absent (Advisor finding, this
  // build's report): dataService.js defaults a genuinely-unresolved author_name to the literal
  // placeholder string 'Unknown Author' on Locality (dataService.js:949/1004/1107/1390) or to the
  // raw author_id UUID on Group News (dataService.js:1187) rather than to null/''. Rendering
  // either verbatim would be exactly the placeholder text D9 rules out ("no placeholder text, no
  // em-dash filler") — so both are treated as absent here, at this presentation layer, rather
  // than changed at the data layer (out of this build's frontend-only scope, and 'Unknown Author'
  // is relied on elsewhere as intentional copy, e.g. ArticleDetail's byline — not safe to change
  // globally from here).
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const rawAuthorName = article.author_name || '';
  const authorName = (rawAuthorName && rawAuthorName !== 'Unknown Author' && !UUID_RE.test(rawAuthorName))
    ? rawAuthorName
    : '';

  const commentCount = article.comment_count || 0;
  const isVideoArticle = !!article.video_url;

  return (
    <div
      onClick={onCollapse}
      style={{
        background: 'white',
        borderRadius: `${tokens.card.radius.list}px`,
        border: `${BORDER_WIDTH}px solid ${theme.primary}`,
        boxShadow: tokens.card.shadow.resting,
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        minWidth: 0,
        cursor: onCollapse ? 'pointer' : 'default'
      }}
    >
      {/* Image — COLLAPSE. Wrapped in a positioning context only when there's a play-hint to
          anchor, so the plain image case (the vast majority of articles) is unchanged. */}
      {imageUrl && (
        <div style={{ position: 'relative' }}>
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            style={{
              width: '100%',
              maxHeight: '220px',
              objectFit: 'cover',
              borderRadius: `${tokens.card.radius.list}px`,
              backgroundColor: '#f3f4f6',
              display: 'block'
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {/* Play-hint — video articles only. Own click target: stops propagation so it opens
              Detail (onViewDetails, the same route "More" uses) instead of also collapsing the
              card via the container's onClick above. */}
          {isVideoArticle && (
            <button
              type="button"
              aria-label={t('card_play_video')}
              onClick={(e) => { e.stopPropagation(); onViewDetails?.(); }}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '52px',
                height: '52px',
                borderRadius: '50%',
                border: 'none',
                background: tokens.colors.backdrop,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                transition: 'background 0.15s, transform 0.1s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0, 0, 0, 0.65)'; e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)'; e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)'; }}
            >
              <Play size={24} color="white" fill="white" strokeWidth={0} style={{ marginLeft: '3px' }} />
            </button>
          )}
        </div>
      )}

      {/* Title — HOLD */}
      <div
        style={{
          fontFamily: 'OpenSauceSans, sans-serif',
          fontWeight: '700',
          fontSize: '16px',
          color: '#1f2937',
          lineHeight: '1.3',
          minHeight: '1.3em'
        }}
      >
        {article.title || ''}
      </div>

      {/* Introduction — COLLAPSE */}
      {introduction && (
        <div
          style={{
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '13px',
            color: '#4b5563',
            lineHeight: '1.5'
          }}
        >
          {introduction}
        </div>
      )}

      {/* Author · comment count — COLLAPSE, each half independently */}
      {(authorName || commentCount > 0) && (
        <div
          style={{
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '12px',
            fontWeight: '600',
            color: '#6b7280',
            display: 'flex',
            gap: '6px',
            alignItems: 'center'
          }}
        >
          {authorName && <span>{authorName}</span>}
          {authorName && commentCount > 0 && <span>·</span>}
          {commentCount > 0 && <span>{commentCount} {t('detail:comments')}</span>}
        </div>
      )}

      {/* "More" — unconditional (see file header). Stops propagation so it navigates instead of
          also collapsing the card via the container's onClick above. */}
      <div style={{ marginTop: '2px' }} onClick={(e) => e.stopPropagation()}>
        <StandardButton
          variant="inline-emphasis"
          iconName="ChevronRight"
          label={t('card_more')}
          onClick={onViewDetails}
          inlineTextColor={theme.primary}
        />
      </div>
    </div>
  );
};

export default NewsMiniCard;
